import React, { useState } from "react";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";

import FastForwardIcon from "@mui/icons-material/FastForward";

import Checkbox from "components/Checkbox";
import InfosTooltip from "components/InfosTooltip";

import MapConfig from "services/map/MapConfig";
import ParkkiTheme from "themes/ParkkiTheme";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  padding: 0px 10px 0px 10px;
  cursor: ${(props: { disabled: boolean }) =>
    props.disabled ? "not-allowed" : "pointer"};
  border: 0.5px solid;
  border-radius: 6px;
  opacity: ${(props: { disabled: boolean }) => (props.disabled ? 0.4 : 1)};
  min-height: 43px;
`;

const LiveLabel = styled.div`
  margin-right: ${ParkkiTheme.spacings.tiny};
  margin-left: -24px;
`;

const StyledIcon = styled(FastForwardIcon)`
  width: 18px;
  height: auto;
`;

type Props = {
  onChecked: Function;
  onReload: Function;
  withLiveReload: boolean;
  className?: string;
};

function LiveButton({
  onChecked,
  withLiveReload,
  onReload,
  className,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();
  const [disabled, setDisabled] = useState(false);

  // Use a tempo to disable pickers during retrieval
  // Unable to fix this bug properly (faster to do this way)
  // https://parkki.atlassian.net/browse/CLT-26
  function temporize(ms = MapConfig.timePickers.tempo) {
    setDisabled(true);
    setTimeout(() => setDisabled(false), ms);
  }

  function handleReload() {
    if (disabled) return null;

    temporize();
    return onReload();
  }

  function handleChecked(event: React.ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    onChecked(!withLiveReload);
  }

  return (
    <Container className={className} disabled={disabled} onClick={handleReload}>
      <InfosTooltip
        withImage={false}
        infos={formatMessage({ id: "components.buttons.liveCheckbox" })}
      >
        <Checkbox
          disabled={disabled}
          checked={withLiveReload}
          onChange={handleChecked}
        />
      </InfosTooltip>

      <LiveLabel>{formatMessage({ id: "components.buttons.live" })}</LiveLabel>
      <StyledIcon />
    </Container>
  );
}

export default LiveButton;
